import el from "@cypherpotato/el";
import { copyToClipboard } from "../ui/clipboard";
import { normalizeWhitespace } from "../util";
import { Icon } from "./icon";

export function MutedText(...args) {
    return el("span.muted-text", ...args);
}

export function GhostedText(...args) {
    return el("span.ghosted-text", ...args);
}

export function TextViewer(text) {
    return el("textarea.text-viewer[readonly=true][rows=3]",
        text?.trim()
    );
}

export function CopiableText(visibleText, copyText) {
    if (!visibleText && !copyText)
        return null;

    return el("span.copiable-text",
        visibleText,
        Icon("file-copy-line"),
        {
            onClick() {
                copyToClipboard(copyText || visibleText);
            },
            title: copyText || visibleText
        }
    );
}

export function HorizontalBar() {
    return el("hr");
}

export function Heading(title, ...paragraphs) {
    return el(".heading",
        el("h2", title),
        el(".sub-text", ...paragraphs)
    )
}

export function ButtonHeading(icon, title, ...paragraphs) {
    return el(".button-heading",
        icon,
        el("h3", title),
        el(".sub-text", ...paragraphs)
    )
}

export function TextHeading(icon, title, ...paragraphs) {
    return el(".text-heading",
        icon,
        el("div",
            el("h3", title),
            el(".sub-text", ...paragraphs)
        )
    )
}

export function DashboardTextHeading(title, value) {
    return el(".dashboard-text-heading",
        el("h3", value),
        el("p", title),
    )
}

export function Paragraph(...children) {
    return el("p", ...children);
}

export function ParagraphGroup(...children) {
    return el("div.paragraph-group", ...children);
}

export function InlineCode(text) {
    return el("code", text);
}

export function MultilingualCodeWrapper(title, blocks) {

    const selectedLanguage = el.state.create(blocks[0]);

    return el("div.block-code-wrapper",
        el("div.block-code-header",
            el("div.code-title", title),
            el("div.code-actions",
                el("select",
                    blocks.map((s, i) =>
                        el("option", s.label || s.language, {
                            value: i
                        })
                    ),
                    {
                        onChange(event) {
                            selectedLanguage.value = blocks[parseInt(event.target.value)];
                        }
                    }
                ),
                el("button.copy-button", Icon("file-copy-line"),
                    {
                        onClick() {
                            copyToClipboard(selectedLanguage.value.code.trim());
                        }
                    }
                ),
                el("button.download-button", Icon("download-line"),
                    {
                        onClick() {
                            const codeNormalized = normalizeWhitespace(selectedLanguage.value.code);
                            const blob = new Blob([codeNormalized], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = (title || 'code') + (selectedLanguage.value.language ? `.${selectedLanguage.value.language}` : '.txt');
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                        }
                    }
                )
            )
        ),
        el.state.listening([selectedLanguage], () =>
            BlockCode(
                selectedLanguage.value.code.trim(),
                selectedLanguage.value.language
            )
        )
    );
}

export function BlockCodeWrapper(title, code, language) {
    const codeNormalized = normalizeWhitespace(code);
    return el("div.block-code-wrapper",
        el("div.block-code-header",
            el("div.code-title", title),
            el("div.code-actions",
                el("div.code-language", language),
                el("button.copy-button", Icon("file-copy-line"),
                    {
                        onClick() {
                            copyToClipboard(codeNormalized);
                        }
                    }
                ),
                el("button.download-button", Icon("download-line"),
                    {
                        onClick() {
                            const blob = new Blob([codeNormalized], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = (title || 'code') + (language ? `.${language}` : '.txt');
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                        }
                    }
                )
            )
        ),
        BlockCode(codeNormalized, language)
    );
}

export function BlockCode(text, language) {
    return el("pre.wrapped",
        el("code.language-" + language, text, {
            mount() {
                if (language && Prism) {
                    Prism.highlightAll();
                }
            }
        })
    );
}

export function CheckWrapper(state, enabledText, disabledText) {
    return el(".state-wrapper",
        el(".state-text",
            state ? (enabledText || "Enabled") : (disabledText || "Disabled")
        ),
        el(".state-pill",
            el("span")
        ),
        {
            classList: [state ? "state-true" : "state-false"]
        }
    )
}

export function LightTitle(title) {
    return el("h1.light-title", title);
}